import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MirrorColumn } from "../components/MirrorColumn";
import { ConsoleColumn } from "../components/ConsoleColumn";
import { DataColumn } from "../components/DataColumn";
import { ChatSidebar } from "../../common/components/ChatSidebar";
import { FullscreenButton } from "../../common/components/FullscreenButton";
import { CustomDialog } from "../../common/components/CustomDialog";
import { BugReportButton } from "../../common/components/BugReportButton";
import { LanguageToggle } from "../../common/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Sidebar, SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { ConnectionStatusWidget } from "../../common/components/ConnectionStatusWidget";
import { Home } from "lucide-react";
import { UserMenu } from "../../common/components/UserMenu";
import { Story, LogEntry, RundownItem } from "../types";
import { compareLogs } from "../../../utils/logOrder";
import {
  findActiveStoryForUser,
  preserveMissingActiveStories,
  reconcileStoryStatus,
} from "../utils/activeStory";
import { dedupeLogsById, isRealLogId, mergeLogsPreservingPending } from "../utils/logSync";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import { socketService } from "../../../services/socket";
import { logBuffer } from "../../../services/logBuffer";
import { clog } from "../../../services/clientLogger";
import { Sentry } from "../../../sentry";
import { useAuth } from "../../auth/contexts/AuthContext";
import { SummaryFullView } from "../../executive/views/SummaryFullView";
const StoryProducer = React.lazy(() =>
  import("../../storyproducer/views/StoryProducer").then((m) => ({ default: m.StoryProducer })),
);
// Removing INITIAL_PARTICIPANTS mock fallback
import { useTags } from "../../common/contexts/TagsContext";
import { useProjects } from "../../common/contexts/ProjectContext";
import { useChat } from "../../common/contexts/ChatContext";
import { useIsMobile } from "@/hooks/use-mobile";
import logo from "../../../assets/app-logo.png";

// NOTE: localStorage caching for rundowns/stories/participants has been removed.
// The server is the single source of truth — state is populated via socket init-data events.

const MIN_SIDEBAR_WIDTH = 280;
const MAX_SIDEBAR_WIDTH = 640;
const OPTIMISTIC_LOG_TTL_MS = 60_000;
const DELETED_LOG_TOMBSTONE_TTL_MS = 2 * 60_000;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type IdMapping = { oldId: string; newId: string };

const isRealUUID = (value?: string | null) => !!value && UUID_REGEX.test(value);

const applyStoryIdMappings = (stories: Story[], mappings: IdMapping[]) => {
  if (!Array.isArray(mappings) || mappings.length === 0) return stories;
  const idMap = new Map(mappings.map((m) => [m.oldId, m.newId]));
  const remapId = (id?: string) => (id ? idMap.get(id) || id : id);
  const mergedById = new Map<string, Story>();

  for (const story of stories) {
    const storyId = remapId(story.id) || story.id;
    // Dedupe after remapping: the server collapses two optimistic logs that
    // land on the same uq_logs_dedup slot into one row, so both temp ids map
    // to the SAME uuid and the story would end up with twin cards.
    const nextLogs = dedupeLogsById(
      (story.logs || []).map((log) => {
        const nextStoryId = remapId(log.storyId) || storyId;
        return {
          ...log,
          id: remapId(log.id) || log.id,
          storyId: nextStoryId,
        };
      }),
    );
    const nextStory: Story = { ...story, id: storyId, logs: nextLogs };
    const existing = mergedById.get(storyId);
    if (!existing) {
      mergedById.set(storyId, nextStory);
      continue;
    }

    mergedById.set(storyId, {
      ...existing,
      ...nextStory,
      logs: dedupeLogsById([...(existing.logs || []), ...nextLogs]),
    });
  }

  return Array.from(mergedById.values());
};

const SidebarResizeHandle: React.FC<{
  side: "left" | "right";
  width: number;
  onChange: (next: number) => void;
  onDragChange?: (dragging: boolean) => void;
}> = ({ side, width, onChange, onDragChange }) => {
  const startRef = useRef<{ x: number; w: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, w: width };
    onDragChange?.(true);
    const onMove = (ev: PointerEvent) => {
      if (!startRef.current) return;
      const delta = ev.clientX - startRef.current.x;
      const raw = side === "left" ? startRef.current.w + delta : startRef.current.w - delta;
      const clamped = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, raw));
      onChange(clamped);
    };
    const onUp = () => {
      startRef.current = null;
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      onDragChange?.(false);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      onPointerDown={onPointerDown}
      className={`group/resize absolute top-0 bottom-0 z-20 flex w-2 cursor-col-resize items-stretch justify-center ${side === "left" ? "-right-1" : "-left-1"}`}
    >
      <div className="w-px bg-[var(--color-panel-border)] transition-colors group-hover/resize:w-[3px] group-hover/resize:bg-[var(--color-role-logger)]" />
    </div>
  );
};

export const LoggerLayout: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // Basic States
  const { navigate } = useProjectNavigation();
  const { user, isLoading: authLoading } = useAuth();
  const { activeProject, clearActiveProject } = useProjects();

  // Redirect to login if session is lost while on this screen.
  // Without this, the user stays on a "logged-in" UI with user=null,
  // which silently produces orphan stories (userId: undefined) and
  // disconnected-socket emits.
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const [rundownItems, setRundownItems] = useState<RundownItem[]>([]);
  const [activeRundownId, setActiveRundownId] = useState<string>("");
  const [activeRundownName, setActiveRundownName] = useState<string>("");
  const loggerName = user?.name || "Unknown";
  const { cameras: allCameras, applyData: applyTagsData } = useTags();
  useChat();
  const [dataReady, setDataReady] = useState(false);
  const [globalAlert, setGlobalAlert] = useState<{
    isOpen: boolean;
    message: string;
    title?: string;
  } | null>(null);

  const [participants, setParticipants] = useState<any[]>([]);
  const [banks, setBanks] = useState<any[]>([]);
  const [stories, setStoriesInternal] = useState<Story[]>([]);
  const storiesRef = useRef<Story[]>([]);
  const setStoriesState = useCallback((update: React.SetStateAction<Story[]>) => {
    const next =
      typeof update === "function"
        ? (update as (prev: Story[]) => Story[])(storiesRef.current)
        : update;
    storiesRef.current = next;
    setStoriesInternal(next);
  }, []);
  const [sideLogs, setSideLogs] = useState<LogEntry[]>([]);
  const [clearSignal, setClearSignal] = useState(0);
  const activeRundownIdRef = useRef(activeRundownId);
  // Socket listeners are registered once with [] deps, so they close over the
  // initial `user`. Mirror it into a ref so handleStoriesUpdate can guard the
  // user's own active story against remote merges that try to flip its status.
  const userRef = useRef(user);

  // Debounced update-stories emit. Every story state change used to emit
  // immediately, re-sending the full logs array of the changed story; a burst
  // of edits (or a reconnect replay) fired dozens of emits per second. We now
  // coalesce by story id — the latest full snapshot supersedes earlier ones —
  // and flush after a short quiet window. Payloads stay FULL (logs intact) so
  // the server broadcast/merge to other clients is unaffected.
  const emitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingEmitRef = useRef<Map<string, Story>>(new Map());
  const optimisticLogIdsRef = useRef<Map<string, number>>(new Map());
  const deletedLogIdsRef = useRef<Map<string, number>>(new Map());
  const deleteRequestedIdsRef = useRef<Set<string>>(new Set());

  const isDeletedLog = useCallback((logId: string) => {
    const deletedAt = deletedLogIdsRef.current.get(logId);
    if (deletedAt === undefined) return false;
    if (Date.now() - deletedAt <= DELETED_LOG_TOMBSTONE_TTL_MS) return true;
    deletedLogIdsRef.current.delete(logId);
    deleteRequestedIdsRef.current.delete(logId);
    return false;
  }, []);

  const requestServerLogDelete = useCallback((logId: string) => {
    if (!isRealLogId(logId) || deleteRequestedIdsRef.current.has(logId)) return;
    deleteRequestedIdsRef.current.add(logId);
    socketService.emit("delete-log", logId, (ack: any) => {
      if (ack?.ok) return;
      deleteRequestedIdsRef.current.delete(logId);
      console.warn("[delete-log] server rejected:", ack?.error);
      Sentry.captureMessage("[delete-log] server rejected", {
        level: "warning",
        extra: { logId, error: ack?.error },
      });
    });
  }, []);

  const pruneOptimisticLogIds = useCallback((now = Date.now()) => {
    for (const [id, seenAt] of optimisticLogIdsRef.current) {
      if (now - seenAt > OPTIMISTIC_LOG_TTL_MS) optimisticLogIdsRef.current.delete(id);
    }
  }, []);

  const rememberOptimisticLogs = useCallback(
    (changedStories: Story[]) => {
      const now = Date.now();
      pruneOptimisticLogIds(now);
      for (const story of changedStories) {
        for (const log of story.logs || []) {
          if (log.id) optimisticLogIdsRef.current.set(log.id, now);
        }
      }
    },
    [pruneOptimisticLogIds],
  );

  const rememberOptimisticMappings = useCallback(
    (mappings: IdMapping[]) => {
      if (!Array.isArray(mappings) || mappings.length === 0) return;
      const now = Date.now();
      pruneOptimisticLogIds(now);
      for (const mapping of mappings) {
        if (
          optimisticLogIdsRef.current.has(mapping.oldId) ||
          (mapping.oldId && !isRealUUID(mapping.oldId))
        ) {
          optimisticLogIdsRef.current.delete(mapping.oldId);
          optimisticLogIdsRef.current.set(mapping.newId, now);
        }
      }
    },
    [pruneOptimisticLogIds],
  );

  const shouldPreserveOptimisticLog = useCallback((log: LogEntry) => {
    if (!log.id) return false;
    const seenAt = optimisticLogIdsRef.current.get(log.id);
    if (seenAt === undefined) return false;
    const now = Date.now();
    if (now - seenAt > OPTIMISTIC_LOG_TTL_MS) {
      optimisticLogIdsRef.current.delete(log.id);
      return false;
    }
    return true;
  }, []);

  useEffect(() => {
    storiesRef.current = stories;
  }, [stories]);

  const flushPendingStories = useCallback(async (waitForProjectReady = true) => {
    if (emitTimerRef.current) {
      clearTimeout(emitTimerRef.current);
      emitTimerRef.current = null;
    }
    if (pendingEmitRef.current.size === 0) return;
    const batch = [...pendingEmitRef.current.values()];
    pendingEmitRef.current = new Map();
    if (
      waitForProjectReady &&
      socketService.connected &&
      typeof socketService.waitForProjectReady === "function"
    ) {
      await socketService.waitForProjectReady();
      if (!socketService.connected) {
        for (const story of batch) pendingEmitRef.current.set(story.id, story);
        return;
      }
    }
    socketService.emit("update-stories", batch);
  }, []);

  // Keep ref in sync for socket listeners
  useEffect(() => {
    activeRundownIdRef.current = activeRundownId;
  }, [activeRundownId]);

  // Never strand a pending batch: flush on unmount and on tab close/refresh.
  useEffect(() => {
    const onBeforeUnload = () => void flushPendingStories(false);
    window.addEventListener("beforeunload", onBeforeUnload);
    const unsubscribe = socketService.onConnectionChange((connected) => {
      if (connected && pendingEmitRef.current.size > 0) {
        void flushPendingStories();
      }
    });
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      unsubscribe();
      void flushPendingStories();
    };
  }, [flushPendingStories]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // When activeProject changes, reset state — server will repopulate via init-data
  useEffect(() => {
    setRundownItems([]);
    setActiveRundownId("");
    setActiveRundownName("");
    setParticipants([]);
    setStoriesState([]);
    setSideLogs([]);
    setDataReady(false);
  }, [activeProject?.id, setStoriesState]);

  // Sync Wrapper: Automatically sends ONLY CHANGED stories to server (not the entire array).
  // The emit MUST happen outside the setState updater — React StrictMode runs updaters twice
  // for invariance checking, which would double-emit every update-stories in dev.
  const setStories = useCallback(
    (newStories: Story[] | ((prev: Story[]) => Story[]), skipEmit = false) => {
      const prevSnapshot = storiesRef.current;
      const nextSnapshot = typeof newStories === "function" ? newStories(prevSnapshot) : newStories;
      setStoriesState(nextSnapshot);
      if (skipEmit) return;
      // Compute delta after the setState call so the emit fires exactly once.
      const prevMap = new Map(prevSnapshot.map((s) => [s.id, s]));
      const changedStories = nextSnapshot.filter((s) => {
        const old = prevMap.get(s.id);
        if (!old) return true; // New story
        return (
          s.status !== old.status ||
          s.title !== old.title ||
          s.rating !== old.rating ||
          s.summary !== old.summary ||
          s.directorId !== old.directorId ||
          JSON.stringify(s.tags) !== JSON.stringify(old.tags) ||
          JSON.stringify(s.cameras) !== JSON.stringify(old.cameras) ||
          JSON.stringify(s.participants) !== JSON.stringify(old.participants) ||
          s.rundownId !== old.rundownId ||
          JSON.stringify(s.logs) !== JSON.stringify(old.logs)
        );
      });
      if (changedStories.length > 0) {
        rememberOptimisticLogs(changedStories);
        clog.debug("setStories queueing update-stories", {
          count: changedStories.length,
          changed: changedStories.map((s) => ({
            id: s.id?.slice(0, 8),
            title: s.title?.slice(0, 60),
            status: s.status,
            logs: s.logs?.length || 0,
          })),
        });
        // Coalesce: latest full snapshot per story wins, flush after a short
        // quiet window (see flushPendingStories). Bursts collapse to one emit.
        for (const s of changedStories) pendingEmitRef.current.set(s.id, s);
        if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
        emitTimerRef.current = setTimeout(() => void flushPendingStories(), 400);
      }
    },
    [flushPendingStories, rememberOptimisticLogs, setStoriesState],
  );

  // localStorage caching removed — server is single source of truth

  // --- SOCKET HANDLERS ---
  const handleInit = (data: any) => {
    clog.info("handleInit received init-data", {
      activeRundownId: data.activeRundownId,
      rundowns: data.rundowns?.length || 0,
      stories: data.stories?.length || 0,
      participants: data.participants?.length || 0,
      banks: data.banks?.length || 0,
      myActiveStories: (data.stories || []).filter(
        (s: any) => s.status === "active" && s.userId === userRef.current?.id,
      ).length,
      user_id: userRef.current?.id?.slice(0, 8),
      myStoriesAnyStatus: (data.stories || []).filter((s: any) => s.userId === userRef.current?.id)
        .length,
    });
    if (data.rundowns && data.rundowns.length > 0) {
      // DB is the single source of truth — use only the explicit activeRundownId.
      // Never fall back to rundowns[0] if activeRundownId is null.
      const targetRundownId = data.activeRundownId;
      if (targetRundownId) {
        const active = data.rundowns.find((r: any) => r.id === targetRundownId);
        if (active) {
          setRundownItems(active.items || []);
          setActiveRundownId(active.id);
          setActiveRundownName(active.name || "");
        } else {
          // ID persisted in DB but rundown was deleted — clear state
          setRundownItems([]);
          setActiveRundownId("");
          setActiveRundownName("");
        }
      } else {
        // No active rundown set in DB — show empty state
        setRundownItems([]);
        setActiveRundownId("");
        setActiveRundownName("");
      }
    } else {
      setRundownItems([]);
      setActiveRundownId("");
      setActiveRundownName("");
    }
    // Only accept server participants if the array is non-empty to avoid
    // wiping locally-cached participants due to transient DB issues or race conditions
    if (data.participants && Array.isArray(data.participants) && data.participants.length > 0) {
      setParticipants(data.participants);
    }
    // Apply tagsData if included (Logger requests it via include option)
    if (data.tagsData) {
      applyTagsData(data.tagsData);
    }
    setDataReady(true);
    if (data.stories) {
      setStoriesState((prev) => {
        // Never wipe local stories when the backend payload is empty — a transient
        // race on tab wake / reconnect can return data.stories=[] (e.g. activeRundownId
        // momentarily null, or a stale read), and wiping the active story here is
        // what makes "NO ACTIVE STORY" appear mid-logging. Initial mount already
        // resets state via the activeProject.id effect, so keeping prev is safe.
        if (!data.stories || data.stories.length === 0) return prev;
        // Merge strategies: backend data overwrites local if exists, keeps local if backend missing.
        // Preserve local logs when the backend story has none yet (logs are fetched separately
        // via get-story-logs) to avoid a flicker where logs briefly disappear on page load.
        const prevMap = new Map(prev.map((p) => [p.id, p]));
        const mergedBackend = (data.stories as Story[]).map((s) => {
          const local = prevMap.get(s.id);
          if (local) {
            return {
              ...s,
              status: reconcileStoryStatus(local.status, s.status),
              logs: mergeLogsPreservingPending(
                local.logs,
                s.logs,
                shouldPreserveOptimisticLog,
                isDeletedLog,
              ),
            };
          }
          return {
            ...s,
            logs: (s.logs || []).filter((log) => !log.id || !isDeletedLog(log.id)),
          };
        });
        const backendMap = new Map(mergedBackend.map((s) => [s.id, s]));
        const merged = [...prev.filter((p) => !backendMap.has(p.id)), ...mergedBackend];
        return merged;
      });

      // After setting stories, load logs for any active (unclosed) stories by this user
      const myActiveStories = (data.stories as Story[]).filter(
        (s: Story) => s.status === "active" && s.userId === userRef.current?.id,
      );
      if (myActiveStories.length > 0) {
        myActiveStories.forEach((story: Story) => {
          socketService.emit("get-story-logs", { storyId: story.id }, (logs: any[]) => {
            if (logs && logs.length > 0) {
              setStoriesState((prev) =>
                prev.map((s) =>
                  s.id === story.id
                    ? {
                        ...s,
                        logs: mergeLogsPreservingPending(
                          s.logs,
                          logs,
                          shouldPreserveOptimisticLog,
                          isDeletedLog,
                        ),
                      }
                    : s,
                ),
              );
            }
          });
        });
      }
    }
    if (data.banks) setBanks(data.banks);
  };

  const handleRundownItemChange = (data: { id: string; rundown: any }) => {
    setActiveRundownId(data.id || "");
    setRundownItems(data.rundown?.items || []);
    setActiveRundownName(data.rundown?.name || "");
  };

  const handleRundownUpdate = (data: { id: string; items: RundownItem[]; name?: string }) => {
    const hasLive = data.items.some((i) => i.status === "live");

    if (activeRundownIdRef.current === data.id) {
      setRundownItems(data.items);
      if (data.name) {
        setActiveRundownName(data.name);
      }
    } else if (hasLive) {
      setActiveRundownId(data.id);
      setRundownItems(data.items);
      if (data.name) {
        setActiveRundownName(data.name);
      }
    }
  };

  const handleStoriesUpdate = (
    data:
      | Story[]
      | {
          rundownId: string | null;
          stories: Story[];
          partialUpdate?: boolean;
          idMappings?: IdMapping[];
        },
  ) => {
    const normalizedData = Array.isArray(data)
      ? data
      : {
          ...data,
          stories: applyStoryIdMappings(data.stories || [], data.idMappings || []),
        };
    console.log("📡 [stories-updated] Received:", {
      isArray: Array.isArray(normalizedData),
      partialUpdate: !Array.isArray(normalizedData) && (normalizedData as any).partialUpdate,
      rundownId: !Array.isArray(normalizedData) && (normalizedData as any).rundownId,
      storyCount: Array.isArray(normalizedData)
        ? normalizedData.length
        : (normalizedData as any).stories?.length,
      storySummaries: Array.isArray(normalizedData)
        ? normalizedData.map((s: any) => ({
            id: s.id?.slice(0, 8),
            title: s.title,
            hasSummary: !!s.summary,
          }))
        : (normalizedData as any).stories?.map((s: any) => ({
            id: s.id?.slice(0, 8),
            title: s.title,
            hasSummary: !!s.summary,
            summary: s.summary?.slice(0, 50),
          })),
    });

    // Keep local ownership/rundown metadata for the user's optimistic active
    // story, but make completion monotonic: an authoritative completed status
    // must close it, and a stale active snapshot must not reopen it. Logs are
    // preserved when the remote payload has none.
    const mergeRespectingOwnership = (story: Story, updated: any): Story => {
      const myId = userRef.current?.id;
      const isMyActive = !!myId && story.userId === myId && story.status === "active";
      const merged = {
        ...updated,
        id: updated.id || story.id,
        status: reconcileStoryStatus(story.status, updated.status),
        logs: mergeLogsPreservingPending(
          story.logs,
          updated.logs,
          shouldPreserveOptimisticLog,
          isDeletedLog,
        ),
      } as Story;
      if (isMyActive) {
        merged.userId = story.userId;
        merged.rundownId = story.rundownId;
      }
      return merged;
    };
    const withoutDeletedLogs = (story: Story): Story => ({
      ...story,
      logs: (story.logs || []).filter((log) => !log.id || !isDeletedLog(log.id)),
    });

    if (Array.isArray(normalizedData)) {
      setStoriesState((prev) => {
        const updateMap = new Map(normalizedData.map((s) => [s.id, s]));
        const updatedPrev = prev.map((story) => {
          const updated = updateMap.get(story.id);
          if (updated) {
            return mergeRespectingOwnership(story, updated);
          }
          return story;
        });
        const newStories = normalizedData
          .filter((s) => !prev.some((p) => p.id === s.id))
          .map(withoutDeletedLogs);
        return [...updatedPrev, ...newStories];
      });
    } else {
      if (normalizedData.partialUpdate || normalizedData.rundownId === null) {
        console.log("✅ [stories-updated] MERGING partial update into state");
        // Merge specifically these stories into our list without replacing everything, appending new ones.
        setStoriesState((prev) => {
          const updateMap = new Map((normalizedData.stories || []).map((s) => [s.id, s]));

          // First, update existing stories
          const updatedPrev = prev.map((story) => {
            const updated = updateMap.get(story.id);
            if (updated) {
              console.log(
                `  🔄 Merging story "${updated.title}" (${story.id?.slice(0, 8)}), summary: ${!!updated.summary}`,
              );
              return mergeRespectingOwnership(story, updated);
            }
            return story;
          });

          // Second, append any completely new stories from backend
          const existingIds = new Set(prev.map((s) => s.id));
          const newStories = (normalizedData.stories || [])
            .filter((s) => !existingIds.has(s.id))
            .map(withoutDeletedLogs);

          return [...updatedPrev, ...newStories];
        });
      } else if (activeRundownIdRef.current === normalizedData.rundownId) {
        console.log("⚠️ [stories-updated] Full replacement (same rundownId)");
        // Standard rundown full replacement — preserve locally-cached logs when
        // the server payload omits them (broadcasts from getStoriesForRundown /
        // getStoryById always send `logs: []` since logs are fetched separately).
        setStoriesState((prev) => {
          const prevMap = new Map(prev.map((s) => [s.id, s]));
          return normalizedData.stories.map((s) => {
            const local = prevMap.get(s.id);
            if (!local) return withoutDeletedLogs(s);
            return mergeRespectingOwnership(local, s);
          });
        });
      } else {
        console.log("❌ [stories-updated] IGNORED — rundownId mismatch", {
          active: activeRundownIdRef.current,
          received: normalizedData.rundownId,
        });
      }
    }
  };

  const handleLogAdded = (newLog: any) => {
    if (newLog?.id && isDeletedLog(newLog.id)) return;
    setStoriesState((prevStories) => {
      return prevStories.map((story) => {
        if (story.id === newLog.storyId) {
          const existingLogs = story.logs || [];
          const logExists = existingLogs.some((l) => l.id === newLog.id);

          if (logExists) {
            return {
              ...story,
              logs: existingLogs.map((l) => (l.id === newLog.id ? newLog : l)),
            };
          } else {
            return { ...story, logs: [...existingLogs, newLog] };
          }
        }
        return story;
      });
    });
  };

  const handleDataClear = () => {
    setStoriesState([]);
    setSideLogs([]);
    setClearSignal((prev) => prev + 1);
  };

  const handleParticipantsUpdate = (data: any[]) => {
    // Accept any valid array — this event comes from intentional user actions (add/edit/delete)
    if (Array.isArray(data)) {
      setParticipants(data);
    }
  };

  const handleFetchStoryLogs = (storyId: string, filters?: any) => {
    socketService.emit("get-story-logs", { storyId, filters }, (logs: any[]) => {
      if (logs && logs.length > 0) {
        setStoriesState((prev: Story[]) =>
          prev.map((s) =>
            s.id === storyId
              ? {
                  ...s,
                  logs: mergeLogsPreservingPending(
                    s.logs,
                    logs,
                    shouldPreserveOptimisticLog,
                    isDeletedLog,
                  ),
                }
              : s,
          ),
        );
      }
    });
  };

  // --- EFFECTS ---

  // 1. Connection & Project Selection
  useEffect(() => {
    let roomVerifyTimer: ReturnType<typeof setTimeout> | null = null;

    const onConnect = () => {
      if (user?.name) {
        const cleanName = user.name.toUpperCase();
        socketService.emit("identify-logger", { name: cleanName });
      }

      if (activeProject) {
        socketService.selectProject(activeProject.id, { include: ["tagsData"] });
        // Note: select-project already calls sendInitData on the server,
        // so we don't need a separate get-init-data here.

        // Post-connect room verification: after 2s, confirm we're actually in the room.
        // This catches phantom reconnects where select-project succeeded but the socket
        // lost its room membership due to a server-side ping timeout race.
        if (roomVerifyTimer) clearTimeout(roomVerifyTimer);
        roomVerifyTimer = setTimeout(() => {
          if (!socketService.connected || !activeProject) return;
          socketService.emit("verify-room-membership", activeProject.id, (inRoom: boolean) => {
            if (!inRoom) {
              console.warn("⚠️ [Logger] Room verification FAILED — forcing re-join");
              socketService.selectProject(activeProject.id, { include: ["tagsData"] });
            } else {
              console.debug("✅ [Logger] Room verification passed");
            }
          });
        }, 2000);
      }
    };

    socketService.on("connect", onConnect);

    // Initial check if already connected
    if (socketService.connected && activeProject) {
      onConnect();
    }

    return () => {
      socketService.off("connect", onConnect);
      if (roomVerifyTimer) clearTimeout(roomVerifyTimer);
    };
  }, [activeProject, user]);

  // 2. Data Listeners
  useEffect(() => {
    const handleBanksUpdated = (updated: any[]) => setBanks(updated);
    const handleFilteredStories = (data: { stories: Story[]; sideLogs: LogEntry[] }) => {
      setStoriesState((prev) => {
        const remoteStories = Array.isArray(data.stories) ? data.stories : [];
        const localById = new Map(prev.map((story) => [story.id, story]));
        const reconciled = remoteStories.map((remote) => {
          const local = localById.get(remote.id);
          if (!local) return remote;

          return {
            ...remote,
            status: reconcileStoryStatus(local.status, remote.status),
            logs: mergeLogsPreservingPending(
              local.logs,
              remote.logs,
              shouldPreserveOptimisticLog,
              isDeletedLog,
            ),
          };
        });

        // Filter responses are asynchronous and may have been queried before
        // the operator closed/created a story. Never let that stale snapshot
        // remove the current local active story from the Console.
        return preserveMissingActiveStories(prev, reconciled, userRef.current?.id);
      });
      setSideLogs(data.sideLogs);
    };
    const handleRequestAutoAnalysis = (storyId: string) => {
      socketService.emit("analyze-story", storyId);
    };

    socketService.on("init-data", handleInit);
    socketService.on("active-rundown-changed", handleRundownItemChange);
    socketService.on("rundown-updated", handleRundownUpdate);
    socketService.on("stories-updated", handleStoriesUpdate);
    socketService.on("log-added", handleLogAdded); // Nuevo listener
    socketService.on("all-data-cleared", handleDataClear);
    socketService.on("participants-updated", handleParticipantsUpdate);
    socketService.on("banks-updated", handleBanksUpdated);
    socketService.on("filtered-project-stories", handleFilteredStories);

    // Remap temp IDs → real Supabase UUIDs after server persists
    const applyIdMappings = (mappings: IdMapping[]) => {
      if (!Array.isArray(mappings) || mappings.length === 0) return;
      const now = Date.now();
      for (const mapping of mappings) {
        if (!deletedLogIdsRef.current.has(mapping.oldId)) continue;
        deletedLogIdsRef.current.set(mapping.newId, now);
        requestServerLogDelete(mapping.newId);
      }
      rememberOptimisticMappings(mappings);
      setStoriesState((prev) =>
        applyStoryIdMappings(prev, mappings).map((story) => ({
          ...story,
          logs: (story.logs || []).filter((log) => !log.id || !isDeletedLog(log.id)),
        })),
      );
      if (pendingEmitRef.current.size > 0) {
        const nextPending = new Map<string, Story>();
        for (const updatedStory of applyStoryIdMappings(
          Array.from(pendingEmitRef.current.values()),
          mappings,
        )) {
          nextPending.set(updatedStory.id, {
            ...updatedStory,
            logs: (updatedStory.logs || []).filter((log) => !log.id || !isDeletedLog(log.id)),
          });
        }
        pendingEmitRef.current = nextPending;
      }
    };
    const handleIdRemap = (mappings: IdMapping[]) => {
      applyIdMappings(mappings);
    };
    socketService.on("stories-id-remapped", handleIdRemap);

    // Server-side persistence failures: report silently to Sentry instead of
    // popping a modal — the alert was noisy for users and the retry path
    // typically recovers on its own.
    const handleStoriesAck = (ack: any) => {
      if (!ack) return;
      applyIdMappings(ack.mappings);
      if (ack.ok === true) {
        return;
      }
      if (ack.deduped) return;
      console.error("[update-stories-ack] Server reported failure:", ack);
      Sentry.captureMessage("[update-stories-ack] persistence failure", {
        level: "error",
        extra: {
          ackError: ack.error,
          errors: ack.errors,
          logErrors: ack.logErrors,
          logErrorDetails: ack.logErrorDetails,
          count: ack.count,
          remapped: ack.remapped,
        },
      });
    };
    socketService.on("update-stories-ack", handleStoriesAck);

    // Auto-Analysis Request from Server
    socketService.on("request-auto-analysis", handleRequestAutoAnalysis);

    return () => {
      socketService.off("init-data", handleInit);
      socketService.off("active-rundown-changed", handleRundownItemChange);
      socketService.off("rundown-updated", handleRundownUpdate);
      socketService.off("stories-updated", handleStoriesUpdate);
      socketService.off("log-added", handleLogAdded); // Cleanup
      socketService.off("all-data-cleared", handleDataClear);
      socketService.off("participants-updated", handleParticipantsUpdate);
      socketService.off("banks-updated", handleBanksUpdated);
      socketService.off("filtered-project-stories", handleFilteredStories);
      socketService.off("stories-id-remapped", handleIdRemap);
      socketService.off("update-stories-ack", handleStoriesAck);
      socketService.off("request-auto-analysis", handleRequestAutoAnalysis);
    };
  }, []);

  // 3. Rundown Freshness Sync — Ensures the Logger always has the latest rundown
  //    even if socket events were missed (tab sleep, transient disconnect, etc.)
  useEffect(() => {
    if (!activeProject) return;

    // Lightweight: ask the server for the current active rundown and reconcile
    const syncRundownFreshness = () => {
      if (!socketService.connected) return;

      socketService.emit("get-active-rundown", (response: any) => {
        if (!response?.success || !response.activeRundownId) return;

        const serverRundownId = response.activeRundownId;
        const serverRundown = response.rundown;

        // Only switch rundowns if our current rundown has NO live items,
        // or if the server explicitly tells us a different rundown is active.
        // This prevents glitching back to an old event if the backend is lagging.
        const currentHasLive = rundownItems.some((i) => i.status === "live");

        if (
          serverRundownId !== activeRundownIdRef.current &&
          (!currentHasLive || serverRundown?.items?.some((i: any) => i.status === "live"))
        ) {
          console.debug(
            `🔄 [Logger] Rundown sync: local=${activeRundownIdRef.current?.slice(0, 8)}, server=${serverRundownId.slice(0, 8)} → updating`,
          );
          setActiveRundownId(serverRundownId);
          setRundownItems(serverRundown?.items || []);
          setActiveRundownName(serverRundown?.name || "");
        } else if (serverRundownId === activeRundownIdRef.current && serverRundown?.items) {
          // Same rundown but items may have changed (e.g., a live/inactive status change)
          setRundownItems((prev) => {
            const prevKey = prev.map((i) => `${i.id}:${i.status}`).join(",");
            const serverKey = serverRundown.items.map((i: any) => `${i.id}:${i.status}`).join(",");
            // Only update if the server actually has a live event, or if our local state doesn't have one either
            const serverHasLive = serverRundown.items.some((i: any) => i.status === "live");
            if (prevKey !== serverKey && (serverHasLive || !currentHasLive)) {
              console.debug(
                `🔄 [Logger] Rundown items sync: same rundown but items changed → updating`,
              );
              return serverRundown.items;
            }
            return prev;
          });
        }
      });
    };

    // A) Tab wake recovery — sync immediately when the tab becomes visible
    //    Also re-join the project room to recover from Chrome tab throttling
    //    which can silently drop the socket's room membership.
    const onVisibilityChange = () => {
      if (!document.hidden && socketService.connected && activeProject) {
        console.debug("[Logger] 👁️ Tab woke up — syncing rundown freshness + re-joining room");
        socketService.selectProject(activeProject.id);
        syncRundownFreshness();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // B) Periodic heartbeat — every 30s, verify the rundown is still current
    const heartbeatInterval = setInterval(syncRundownFreshness, 30_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(heartbeatInterval);
    };
  }, [activeProject]);

  // FILTERED LISTS BY BANK
  const filteredParticipants = React.useMemo(() => {
    if (!user) return participants;
    if (banks.length === 0) return participants;

    const myBank = banks.find((b: any) => String(b.userId) === String(user.id));
    if (!myBank || !myBank.participants) return participants;
    try {
      const allowedIds = JSON.parse(myBank.participants);
      if (!Array.isArray(allowedIds) || allowedIds.length === 0) return participants;
      const filtered = participants.filter((p) => allowedIds.includes(p.id));
      return filtered;
    } catch {
      return participants;
    }
  }, [user, banks, participants]);

  const filteredCameras = React.useMemo(() => {
    if (!user || banks.length === 0) return allCameras;
    const myBank = banks.find((b: any) => String(b.userId) === String(user.id));
    if (!myBank || !myBank.cameras) return allCameras;
    try {
      const allowedIds = JSON.parse(myBank.cameras);
      if (!Array.isArray(allowedIds) || allowedIds.length === 0) return allCameras;
      const filtered = allCameras.filter((c) => allowedIds.includes(c.id));
      return filtered;
    } catch {
      return allCameras;
    }
  }, [user, banks, allCameras]);

  // Each logger only sees THEIR OWN stories in the sidebar — never other users'.
  // Filtering here (instead of inside DataColumn) keeps DataColumn reusable for
  // the Controller view, which still needs the unfiltered list.
  const myStories = React.useMemo(() => {
    if (!user?.id) return [];
    return stories.filter((s) => s.userId === user.id);
  }, [stories, user?.id]);

  // Helper Variables: Each logger should only find THEIR active story
  const activeRundownItem = React.useMemo(() => {
    const liveItem = rundownItems.find((i) => i.status === "live");
    return liveItem || rundownItems[0];
  }, [rundownItems]);
  const activeStory = findActiveStoryForUser(stories, user?.id) || {
    id: "EMPTY",
    title: t("logger.layout.noActiveStory"),
    status: "active",
    author: loggerName || t("logger.layout.system"),
    userId: user?.id,
    createdAt: Date.now(),
    logs: [],
  };

  // Diagnostic: warn whenever activeStory falls back to EMPTY, with the state
  // snapshot that produced it. Lets us tell apart the failure modes:
  //   - user is null/undefined → auth race
  //   - stories has my stories but all status !== "active" → status got flipped
  //   - stories has NO entries with my userId → state was wiped / userId mismatch
  //   - stories is empty → state was wiped (handleInit / handleDataClear / project reset)
  const prevActiveIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = (activeStory as any)?.id || null;
    if (id === "EMPTY" && prevActiveIdRef.current !== "EMPTY") {
      const mine = stories.filter((s) => s.userId === user?.id);
      clog.warn("activeStory FELL BACK TO EMPTY", {
        user_id: user?.id?.slice(0, 8),
        storiesTotal: stories.length,
        myStoriesCount: mine.length,
        myStatuses: mine.map((s) => ({
          id: s.id?.slice(0, 8),
          status: s.status,
        })),
      });
    } else if (id !== "EMPTY" && prevActiveIdRef.current === "EMPTY") {
      clog.info("activeStory recovered", { storyId: id?.slice(0, 8) });
    }
    prevActiveIdRef.current = id;
  });

  // --- CORE HANDLERS ---
  // Resolve the milestone (event) that new content must be persisted under.
  // The JOINED event — `viewingMilestoneId`, owned by ConsoleColumn and only
  // advanced on an explicit "JOIN LIVE" — is the source of truth and must win
  // over the currently-live milestone. Otherwise a rundown change (which
  // auto-advances the live milestone WITHOUT a join, see handleRundownUpdate)
  // would silently move an in-progress story to the new event. This mirrors
  // ConsoleColumn, which already sends `viewingMilestoneId` on every log and
  // new story. Falls back to the live milestone only when no event was joined.
  const resolveMilestoneId = useCallback(() => {
    const joined =
      typeof window !== "undefined" ? localStorage.getItem("viewingMilestoneId") || "" : "";
    if (joined) return joined;
    const liveMilestone = rundownItems.find((i) => i.status === "live");
    return liveMilestone ? liveMilestone.id : rundownItems[0]?.id || "UNGROUPED";
  }, [rundownItems]);

  // Returns true when the log was actually appended to local state, false on
  // any abort path. Callers (submitLog, drop handler) use this to decide
  // whether to clear the input — otherwise the input clears even on failure
  // and the user perceives "my log got swallowed".
  const handleAddLog = useCallback(
    (newLog: LogEntry): boolean => {
      if (!user?.id) {
        Sentry.captureMessage("[handleAddLog] aborted: no user.id", {
          level: "error",
          tags: { component: "LoggerLayout", reason: "no_user_id" },
          extra: { loggerName, hasNewLog: !!newLog, logText: newLog?.text?.slice(0, 80) },
        });
        setGlobalAlert({
          isOpen: true,
          title: t("logger.layout.sessionExpiredTitle", "Sesión expirada"),
          message: t(
            "logger.layout.sessionExpiredMsg",
            "Tu sesión se cerró. Volvé a iniciar sesión para continuar logueando.",
          ),
        });
        return false;
      }
      const activeMilestoneId = resolveMilestoneId();

      let appliedStoryId: string | null = null;
      let activeCountBefore = 0;
      let createdNew = false;

      setStories((prevStories: Story[]) => {
        const activeForMe = prevStories.filter(
          (s) => s.status === "active" && s.userId === user?.id,
        );
        activeCountBefore = activeForMe.length;

        // Look for MY active story in the most recent state. Must match the
        // derivation used by `activeStory` above (reverse + find), otherwise
        // logs get appended to a different active story than the one being
        // rendered in RecentLogsPanel — they vanish from the user's view.
        const myActiveStory = findActiveStoryForUser(prevStories, user?.id);
        let updatedStories = [...prevStories];

        if (!myActiveStory) {
          // Auto-create a story if none exists for me
          clog.warn("handleAddLog AUTO-CREATING new story — no active story found", {
            user_id: user?.id?.slice(0, 8),
            prevStoriesCount: prevStories.length,
            myActiveCount: activeCountBefore,
            firstFiveStories: prevStories.slice(0, 5).map((s) => ({
              id: s.id?.slice(0, 8),
              status: s.status,
              userId: s.userId?.slice(0, 8),
            })),
          });
          const newStoryId = `STORY_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
          const newStory: Story = {
            id: newStoryId,
            title: t("logger.layout.autoStoryTitle"),
            status: "active",
            author: loggerName,
            userId: user?.id,
            createdAt: Date.now(),
            logs: [
              {
                ...newLog,
                author: loggerName,
                storyId: newStoryId,
                rundownId: newLog.rundownId || activeMilestoneId,
                // `startAt` carries the content clock; `createdAt` is only the
                // audit/submission instant. Preserve both from the caller.
                createdAt: newLog.createdAt ?? Date.now(),
              },
            ],
            rundownId: newLog.rundownId || activeMilestoneId,
          };
          updatedStories.push(newStory);
          appliedStoryId = newStoryId;
          createdNew = true;
        } else {
          const logWithAuthor = {
            ...newLog,
            author: loggerName,
            storyId: myActiveStory.id,
            rundownId: newLog.rundownId || activeMilestoneId,
            createdAt: newLog.createdAt ?? Date.now(),
          };
          updatedStories = updatedStories.map((story: Story) => {
            if (story.id === myActiveStory.id) {
              return {
                ...story,
                rundownId: isRealUUID(story.rundownId) ? story.rundownId : logWithAuthor.rundownId,
                logs: [...(story.logs || []), logWithAuthor],
              };
            }
            return story;
          });
          appliedStoryId = myActiveStory.id;
        }
        return updatedStories;
      });

      // Breadcrumb so Sentry can show the timeline leading up to any error
      // captured later (e.g. update-stories-ack failure).
      Sentry.addBreadcrumb({
        category: "logger",
        message: "handleAddLog applied",
        level: "info",
        data: {
          createdNew,
          activeCountBefore,
          appliedStoryId,
          logId: newLog?.id,
        },
      });

      // Sanity invariant: if the user already had >1 active story, the
      // client-side merge with broadcasts let multi-active leak in. The
      // server now closes other actives on every upsert, so this should be
      // impossible — capture if it happens.
      if (activeCountBefore > 1) {
        Sentry.captureMessage("[handleAddLog] multi-active local state", {
          level: "warning",
          tags: { component: "LoggerLayout", reason: "multi_active" },
          extra: { activeCountBefore, appliedStoryId },
        });
      }
      return true;
    },
    [loggerName, resolveMilestoneId, setStories, user?.id, t],
  );

  const handleUpdateLog = useCallback(
    (updatedLog: LogEntry) => {
      const logWithAuthor = { ...updatedLog, author: updatedLog.author || loggerName };
      setStories((prev) =>
        prev.map((story) => {
          if ((story.logs || []).some((l) => l.id === updatedLog.id)) {
            return {
              ...story,
              logs: (story.logs || []).map((l) => (l.id === updatedLog.id ? logWithAuthor : l)),
            };
          }
          return story;
        }),
      );
    },
    [loggerName, setStories],
  );

  // Reorden manual desde Recent Logs (drag & drop). Asigna sortOrder = índice
  // a cada log de la historia que los contiene, en un solo setStories — eso
  // emite UN update-stories y el server persiste content.sort_order en el
  // upsert de cada log. La biblioteca y los exports ordenan con compareLogs,
  // así que el orden ajustado aquí es el que llega a la biblioteca.
  const handleReorderLogs = useCallback(
    (orderedIds: string[]) => {
      if (orderedIds.length === 0) return;
      setStories((prev) =>
        prev.map((story) => {
          if (!(story.logs || []).some((l) => l.id === orderedIds[0])) return story;
          const indexOf = new Map(orderedIds.map((id, i) => [id, i]));
          return {
            ...story,
            logs: (story.logs || []).map((l) =>
              indexOf.has(l.id) ? { ...l, sortOrder: indexOf.get(l.id) } : l,
            ),
          };
        }),
      );
    },
    [setStories],
  );

  const handleDeleteLog = useCallback(
    (logId: string) => {
      deletedLogIdsRef.current.set(logId, Date.now());
      optimisticLogIdsRef.current.delete(logId);

      // Scrub queued snapshots as well as the latest state. A temp log may
      // already be in an in-flight request; if it is later remapped to a UUID,
      // applyIdMappings turns this tombstone into an explicit server delete.
      for (const [storyId, story] of pendingEmitRef.current) {
        if (!(story.logs || []).some((log) => log.id === logId)) continue;
        pendingEmitRef.current.set(storyId, {
          ...story,
          logs: (story.logs || []).filter((log) => log.id !== logId),
        });
      }

      setStories((prev) =>
        prev.map((story) =>
          (story.logs || []).some((log) => log.id === logId)
            ? { ...story, logs: (story.logs || []).filter((log) => log.id !== logId) }
            : story,
        ),
      );
      requestServerLogDelete(logId);
    },
    [requestServerLogDelete, setStories],
  );

  const handleCreateStory = useCallback(
    (title: string, rId?: string) => {
      if (!user?.id) {
        setGlobalAlert({
          isOpen: true,
          title: t("logger.layout.sessionExpiredTitle", "Sesión expirada"),
          message: t(
            "logger.layout.sessionExpiredMsg",
            "Tu sesión se cerró. Volvé a iniciar sesión para continuar logueando.",
          ),
        });
        return;
      }
      const activeMilestoneId = resolveMilestoneId();

      const newStoryId = `STORY_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
      const newStory: Story = {
        id: newStoryId,
        title: title.toUpperCase(),
        status: "active",
        author: loggerName,
        userId: user?.id,
        createdAt: Date.now(),
        logs: [],
        rundownId: rId || activeMilestoneId,
      };

      setStories((prev) => [
        ...prev.map((story) =>
          story.status === "active" && story.userId === user.id
            ? { ...story, status: "complete" as const }
            : story,
        ),
        newStory,
      ]);
    },
    [resolveMilestoneId, loggerName, setStories, user?.id, t],
  );

  const handleCompleteStory = useCallback(
    (id: string) => {
      if (!user?.id) {
        setGlobalAlert({
          isOpen: true,
          title: t("logger.layout.sessionExpiredTitle", "Sesión expirada"),
          message: t(
            "logger.layout.sessionExpiredMsg",
            "Tu sesión se cerró. Volvé a iniciar sesión para continuar logueando.",
          ),
        });
        return;
      }
      const activeMilestoneId = resolveMilestoneId();
      const newStoryId = `STORY_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
      const newStory: Story = {
        id: newStoryId,
        title: t("logger.layout.autoStoryTitle"),
        status: "active",
        author: loggerName,
        userId: user?.id,
        createdAt: Date.now(),
        logs: [],
        rundownId: activeMilestoneId,
      };
      setStories((prev) => [
        ...prev.map((story) =>
          story.id === id ? { ...story, status: "complete" as const } : story,
        ),
        newStory,
      ]);
    },
    [setStories, resolveMilestoneId, loggerName, user?.id, t],
  );

  const handleRegenerateSummary = useCallback(
    (storyId: string) => {
      const projectId = activeProject?.id;
      console.log(`🔄 [regenerate-summary] Emitting for story ${storyId}, project ${projectId}`);
      if (!projectId) {
        console.warn("❌ [regenerate-summary] No active project");
        return;
      }
      socketService.emit("regenerate-summary", { storyId, projectId }, (ack: any) => {
        console.log("📡 [regenerate-summary] ACK:", ack);
      });
    },
    [activeProject?.id],
  );

  const handleUpdateStory = useCallback(
    (updatedStory: Story) => {
      // Use functional updater so we always map over the LATEST state,
      // avoiding stale-closure issues when broadcasts update stories
      // between renders.
      setStories((prev) => prev.map((s) => (s.id === updatedStory.id ? updatedStory : s)));
    },
    [setStories],
  );

  // Server already removed the story + its logs by the time this fires.
  // We just drop it from local state with skipEmit=true so the change does
  // not bounce back through update-stories.
  const handleDeleteStoryLocal = useCallback(
    (storyId: string) => {
      setStoriesState((prev) => prev.filter((s) => s.id !== storyId));
    },
    [setStoriesState],
  );

  const handleAddSideLog = useCallback(
    (newLog: LogEntry) => {
      if (!user?.id) {
        setGlobalAlert({
          isOpen: true,
          title: t("logger.layout.sessionExpiredTitle", "Sesión expirada"),
          message: t(
            "logger.layout.sessionExpiredMsg",
            "Tu sesión se cerró. Volvé a iniciar sesión para continuar logueando.",
          ),
        });
        return;
      }
      const activeMilestoneId = resolveMilestoneId();
      const logToSend = { ...newLog, rundownId: activeMilestoneId, author: loggerName };

      setSideLogs((prev) => [...prev, logToSend]);

      // Log goes through the buffer (persists in localStorage, auto-retries, ACK-confirmed)
      logBuffer.add(logToSend);
    },
    [resolveMilestoneId, loggerName, user?.id, t],
  );

  const handleMoveSideLogToStory = useCallback(
    (logId: string, targetStoryId: string) => {
      const logToMove = sideLogs.find((l) => l.id === logId);
      if (!logToMove) return;

      const activeMilestoneId = resolveMilestoneId();

      if (targetStoryId.startsWith("NEW:")) {
        const storyName = targetStoryId.slice(4) || `Story from Log (${logToMove.tc})`;
        const newStory: Story = {
          id: `STORY_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`,
          title: storyName,
          status: "complete",
          author: loggerName,
          userId: user?.id,
          createdAt: Date.now(),
          logs: [{ ...logToMove, rundownId: activeMilestoneId }],
          rundownId: activeMilestoneId,
        };
        setStories((prev) => [...prev, newStory]);
      } else {
        setStories((prev) =>
          prev.map((s) =>
            s.id === targetStoryId
              ? {
                  ...s,
                  summary: s.status === "complete" ? undefined : s.summary, // Clear summary to trigger re-analysis
                  logs: [...(s.logs || []), { ...logToMove, rundownId: s.rundownId }].sort(
                    compareLogs,
                  ),
                }
              : s,
          ),
        );
      }
      setSideLogs((prev) => prev.filter((l) => l.id !== logId));
    },
    [sideLogs, resolveMilestoneId, setStories, loggerName, user],
  );

  const handleCreateStoryFromLogs = useCallback(
    (logIds: string[], storyName: string) => {
      const logsToMove = sideLogs.filter((l) => logIds.includes(l.id));
      if (logsToMove.length === 0) return;

      const activeMilestoneId = resolveMilestoneId();

      logsToMove.sort(compareLogs);

      const newStory: Story = {
        id: `STORY_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`,
        title: storyName,
        status: "complete",
        author: loggerName,
        userId: user?.id,
        createdAt: Date.now(),
        logs: logsToMove.map((l) => ({ ...l, rundownId: activeMilestoneId })),
        rundownId: activeMilestoneId,
      };

      setStories((prev) => [...prev, newStory]);
      setSideLogs((prev) => prev.filter((l) => !logIds.includes(l.id)));
    },
    [sideLogs, resolveMilestoneId, setStories, loggerName, user],
  );

  // --- GLOBAL EDITING LOGIC ---
  const [globalEditingLog, setGlobalEditingLog] = useState<{
    log: LogEntry;
    source: "active" | "history" | "sweeper";
    contextId?: string;
  } | null>(null);
  const [globalEditingStory, setGlobalEditingStory] = useState<Story | null>(null);

  const handleStartGlobalStoryEdit = useCallback((story: Story) => {
    setGlobalEditingStory(story);
    setGlobalEditingLog(null);
  }, []);

  const handleCancelGlobalStoryEdit = useCallback(() => {
    setGlobalEditingStory(null);
  }, []);

  const handleStartGlobalEdit = useCallback(
    (log: LogEntry, source: "active" | "history" | "sweeper", contextId?: string) => {
      setGlobalEditingLog({ log, source, contextId });
    },
    [],
  );

  const handleGlobalUpdateLog = useCallback(
    (updatedLog: LogEntry) => {
      if (!globalEditingLog) return;
      const { source, contextId } = globalEditingLog;

      if (source === "active") {
        handleUpdateLog(updatedLog);
      } else if (source === "history" && contextId) {
        setStories((prev) =>
          prev.map((s) =>
            s.id === contextId
              ? { ...s, logs: (s.logs || []).map((l) => (l.id === updatedLog.id ? updatedLog : l)) }
              : s,
          ),
        );
      } else if (source === "sweeper") {
        setSideLogs((prev) => prev.map((l) => (l.id === updatedLog.id ? updatedLog : l)));
      }
      setGlobalEditingLog(null);
    },
    [globalEditingLog, handleUpdateLog, setStories],
  );

  const handleCancelGlobalEdit = useCallback(() => setGlobalEditingLog(null), []);

  const handleGlobalDeleteLog = useCallback(() => {
    if (!globalEditingLog) return;
    const { log, source, contextId } = globalEditingLog;

    if (source === "active") {
      setStories((prev) =>
        prev.map((s) =>
          s.status === "active" ? { ...s, logs: (s.logs || []).filter((l) => l.id !== log.id) } : s,
        ),
      );
    } else if (source === "history" && contextId) {
      setStories((prev) =>
        prev.map((s) =>
          s.id === contextId ? { ...s, logs: (s.logs || []).filter((l) => l.id !== log.id) } : s,
        ),
      );
    } else if (source === "sweeper") {
      setSideLogs((prev) => prev.filter((l) => l.id !== log.id));
    }
    setGlobalEditingLog(null);
  }, [globalEditingLog, setStories]);

  const [serverTime, setServerTime] = useState<string>("--:--:--");
  const [isLeftOpen, setIsLeftOpen] = useState<boolean>(() => {
    return localStorage.getItem("logger-left-sidebar-open") === "true";
  });
  const [isRightOpen, setIsRightOpen] = useState<boolean>(() => {
    return localStorage.getItem("logger-right-sidebar-open") === "true";
  });
  const [leftWidth, setLeftWidth] = useState<number>(() => {
    const saved = localStorage.getItem("logger-left-sidebar-width");
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) ? n : 360;
  });
  const [rightWidth, setRightWidth] = useState<number>(() => {
    const saved = localStorage.getItem("logger-right-sidebar-width");
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) ? n : 420;
  });
  const [isLeftDragging, setIsLeftDragging] = useState(false);
  const [isRightDragging, setIsRightDragging] = useState(false);
  const [rightTab, setRightTab] = useState<"logs" | "summaries" | "biblioteca">("logs");

  useEffect(() => {
    localStorage.setItem("logger-left-sidebar-width", String(leftWidth));
  }, [leftWidth]);
  useEffect(() => {
    localStorage.setItem("logger-right-sidebar-width", String(rightWidth));
  }, [rightWidth]);
  useEffect(() => {
    localStorage.setItem("logger-left-sidebar-open", String(isLeftOpen));
  }, [isLeftOpen]);
  useEffect(() => {
    localStorage.setItem("logger-right-sidebar-open", String(isRightOpen));
  }, [isRightOpen]);

  useEffect(() => {
    const timer = setInterval(() => setServerTime(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!dataReady) {
    return (
      <div
        data-testid="logger-loading"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100vw",
          background: "var(--color-bg-mesh)",
          color: "var(--color-text-muted)",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "3px solid var(--color-panel-border)",
            borderTop: "3px solid var(--color-role-logger)",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <span style={{ fontSize: "0.9rem", letterSpacing: "2px" }}>
          {t("common.loading", "LOADING...")}
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      data-testid="logger-page"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        background: "var(--color-bg-mesh)",
      }}
    >
      {/* GLOBAL LOGGER HEADER */}
      <header
        data-testid="logger-header"
        style={{
          boxSizing: "border-box",
          flexShrink: 0,
          height: "88px",
          background: "#0d0d0d",
          borderBottom: "5px solid var(--color-role-logger)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: isMobile ? "0 12px" : "0 40px",
          zIndex: 10,
          position: "relative",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          {!isMobile && (
            <img src={logo} alt="Braindog" className="h-10 w-auto object-contain shrink-0" />
          )}
          <Breadcrumb data-testid="logger-breadcrumb">
            <BreadcrumbList className="text-base">
              {!isMobile && (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      data-testid="logger-btn-projects"
                      onClick={() => {
                        clearActiveProject();
                        navigate("/projects");
                      }}
                      className="cursor-pointer flex items-center"
                      aria-label="Home"
                    >
                      <Home className="h-4 w-4" />
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <BreadcrumbPage
                      data-testid="logger-role-label"
                      style={{ color: "var(--color-role-logger)" }}
                      className="font-semibold"
                    >
                      LOGGER
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
              {activeProject?.name && (
                <BreadcrumbItem>
                  <BreadcrumbPage data-testid="logger-project-badge">
                    {activeProject.name.toUpperCase()}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* CENTER: Navigation Buttons */}
        {!isMobile && (
          <div
            data-testid="logger-nav"
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <div
              data-testid="logger-nav-tabs"
              style={{
                display: "flex",
                gap: "8px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: "12px",
                padding: "6px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <button
                data-testid="logger-nav-logs"
                onClick={() => setRightTab("logs")}
                style={{
                  padding: "8px 20px",
                  background: rightTab === "logs" ? "var(--color-role-logger)" : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: rightTab === "logs" ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-list-ul" style={{ fontSize: "1.2rem" }}></i>
                LOGS
              </button>
              <button
                data-testid="logger-nav-summaries"
                onClick={() => setRightTab("summaries")}
                style={{
                  padding: "8px 20px",
                  background: rightTab === "summaries" ? "var(--color-role-logger)" : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: rightTab === "summaries" ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-bar-chart-alt-2" style={{ fontSize: "1.2rem" }}></i>
                {t("controller.layout.summaries", "SUMMARIES")}
              </button>
              <button
                data-testid="logger-nav-biblioteca"
                onClick={() => setRightTab("biblioteca")}
                style={{
                  padding: "8px 20px",
                  background:
                    rightTab === "biblioteca" ? "var(--color-role-logger)" : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: rightTab === "biblioteca" ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-library" style={{ fontSize: "1.2rem" }}></i>
                {t("controller.layout.explore", "BIBLIOTECA")}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <ButtonGroup>
            <Button
              data-testid="logger-toggle-left"
              variant={isLeftOpen ? "default" : "outline"}
              size="icon"
              onClick={() => setIsLeftOpen((v) => !v)}
              title={t("logger.mirror.rundown")}
              className={
                isLeftOpen
                  ? "bg-[var(--color-role-logger)] text-white hover:bg-[var(--color-role-logger)]/90"
                  : "text-muted-foreground hover:text-foreground"
              }
            >
              <i className="bx bx-dock-left" style={{ fontSize: "1.2rem" }}></i>
            </Button>
            <Button
              data-testid="logger-toggle-right"
              variant={isRightOpen ? "default" : "outline"}
              size="icon"
              onClick={() => setIsRightOpen((v) => !v)}
              title={t("logger.data.history")}
              className={
                isRightOpen
                  ? "bg-[var(--color-role-logger)] text-white hover:bg-[var(--color-role-logger)]/90"
                  : "text-muted-foreground hover:text-foreground"
              }
            >
              <i className="bx bx-dock-right" style={{ fontSize: "1.2rem" }}></i>
            </Button>
            <LanguageToggle />
            <BugReportButton module="logger" />
            <FullscreenButton isInline={true} />
          </ButtonGroup>

          <ConnectionStatusWidget />
          <UserMenu role="logger" testIdPrefix="logger-user" />
        </div>
      </header>

      {/* MAIN GRID */}
      <SidebarProvider
        open={isLeftOpen}
        onOpenChange={setIsLeftOpen}
        data-testid="logger-main-grid"
        className={`!min-h-0 flex-1 overflow-hidden ${isLeftDragging ? "[&_[data-slot=sidebar-gap]]:!transition-none [&_[data-slot=sidebar-container]]:!transition-none" : ""}`}
        style={{ ["--sidebar-width" as any]: `${leftWidth}px` }}
      >
        <Sidebar
          side="left"
          collapsible="offcanvas"
          className="!top-[88px] !h-[calc(100svh-88px)] bg-[var(--color-panel-bg)]"
        >
          <div data-testid="logger-left-column" className="flex h-full flex-col gap-5 p-4">
            <div className="min-h-0 flex-[0_0_45%]">
              <MirrorColumn
                collapsed={false}
                noToggle={true}
                items={rundownItems}
                onSelect={setActiveRundownId}
                serverTime={serverTime}
              />
            </div>
            <div className="min-h-0 flex-1">
              <ChatSidebar embedded={true} />
            </div>
          </div>
          <SidebarResizeHandle
            side="left"
            width={leftWidth}
            onChange={setLeftWidth}
            onDragChange={setIsLeftDragging}
          />
        </Sidebar>

        <SidebarProvider
          open={isRightOpen}
          onOpenChange={setIsRightOpen}
          className={`!min-h-0 flex-1 overflow-hidden ${isRightDragging ? "[&_[data-slot=sidebar-gap]]:!transition-none [&_[data-slot=sidebar-container]]:!transition-none" : ""}`}
          style={{ ["--sidebar-width" as any]: `${rightWidth}px` }}
        >
          <SidebarInset className="overflow-hidden bg-transparent p-2 md:p-5">
            <ConsoleColumn
              activeStory={activeStory}
              activeRundown={activeRundownItem}
              rundownTitle={activeRundownName}
              rundownItems={rundownItems}
              onAddLog={handleAddLog}
              onUpdateLog={handleUpdateLog}
              onDeleteLog={handleDeleteLog}
              onReorderLogs={handleReorderLogs}
              onCreateStory={handleCreateStory}
              onCompleteStory={handleCompleteStory}
              onAddSideLog={handleAddSideLog}
              externalEditingLog={globalEditingLog?.log}
              externalEditingStory={globalEditingStory}
              onExternalUpdate={handleGlobalUpdateLog}
              onExternalCancel={handleCancelGlobalEdit}
              onExternalStoryCancel={handleCancelGlobalStoryEdit}
              onExternalDelete={handleGlobalDeleteLog}
              participants={filteredParticipants}
              loggerName={loggerName}
              onUpdateLoggerName={() => {}}
              onUpdateStory={handleUpdateStory}
              onEditStory={handleStartGlobalStoryEdit}
              clearSignal={clearSignal}
              availableCameras={filteredCameras}
            />
          </SidebarInset>
          <Sidebar
            side="right"
            collapsible="offcanvas"
            className="!top-[88px] !h-[calc(100svh-88px)] bg-[var(--color-panel-bg)]"
          >
            <SidebarResizeHandle
              side="right"
              width={rightWidth}
              onChange={setRightWidth}
              onDragChange={setIsRightDragging}
            />
            <div data-testid="logger-right-column" className="flex h-full min-h-0 flex-col p-4">
              <DataColumn
                stories={myStories}
                activeStoryId={activeStory?.id}
                onUpdateStory={handleUpdateStory}
                onDeleteStory={handleDeleteStoryLocal}
                sideLogs={sideLogs}
                onUpdateSideLogs={setSideLogs}
                onMoveSideLogToStory={handleMoveSideLogToStory}
                onCreateStoryFromLogs={handleCreateStoryFromLogs}
                rundownItems={rundownItems}
                onEditLog={handleStartGlobalEdit}
                onEditStory={handleStartGlobalStoryEdit}
                editingLogId={globalEditingLog?.log.id || null}
                editingStoryId={globalEditingStory?.id || null}
                participants={filteredParticipants}
                loggerName={loggerName}
                onAnalyzeStory={(storyId) => {
                  socketService.emit("analyze-story", storyId);
                }}
                onAnalyzeRundown={(rundownId) => {
                  socketService.emit("ai:summary", { rundownId });
                }}
                onFetchStoryLogs={handleFetchStoryLogs}
              />
            </div>
          </Sidebar>
        </SidebarProvider>
      </SidebarProvider>
      <CustomDialog
        isOpen={!!globalAlert?.isOpen}
        type="alert"
        title={globalAlert?.title || t("logger.layout.attention")}
        message={globalAlert?.message || ""}
        onConfirm={() => setGlobalAlert(null)}
        onCancel={() => setGlobalAlert(null)}
      />

      {/* SUMMARIES FULL-SCREEN OVERLAY */}
      {rightTab === "summaries" && activeProject?.id && (
        <div
          data-testid="logger-summaries-fullscreen"
          style={{
            position: "absolute",
            top: 88,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 90,
            background: "#000",
            overflow: "auto",
            padding: "24px 28px",
          }}
        >
          <SummaryFullView projectId={activeProject.id} />
        </div>
      )}

      {/* BIBLIOTECA FULL-SCREEN OVERLAY */}
      {rightTab === "biblioteca" && activeProject?.id && (
        <div
          data-testid="logger-biblioteca-fullscreen"
          style={{
            position: "absolute",
            top: 88,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 90,
            background: "#000",
            overflow: "hidden",
          }}
        >
          <React.Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "rgba(255,255,255,0.3)",
                }}
              >
                {t("common.loading", "LOADING...")}
              </div>
            }
          >
            <StoryProducer isEmbedded={true} forceReadOnly={true} />
          </React.Suspense>
        </div>
      )}
    </div>
  );
};
